import { ReactNode } from 'react';
import BottomBarWrapper from '@campaigns/src/components/bottom-bar/BottomBarWrapper';
import { Button, Icon } from '@wegroup/design-system';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { FLOWS_PRODUCTS_PATH } from '@flows/src/FlowsProductsRouter';
import useAnvaFlowProductSelectionStore from '@flows/src/store/zustand/flows-products/useAnvaFlowProductSelectionStore';
import BottomBarBasketActions from '@flows/src/components/flow-products/quotes/basket/BottomBarBasketActions';
import BottomBarSelectionOverviewActions from '@flows/src/components/flow-products/quotes/basket/BottomBarSelectionOverviewActions';
import { useAnvaQuotesWithPolling } from '@flows/src/hooks/flow-products';
import useQueryParams from '@common/src/hooks/useQueryParams';
import { isCampaignsUrl } from '../../../../app/src/features/authentication/constants/SocialAuth';
import { useFlowProductsSidebarNavigation } from '@flows/src/hooks/flow-products/useFlowProductsSidebarNavigation';

interface Props {
  handlePrevious?: () => void;
  backText?: string;
}

const AnvaFlowProductsBottomBar = ({ handlePrevious, backText }: Props) => {
  const { t } = useTranslation();
  const { flowId } = useParams<{ flowId: string }>();
  const queryParams = useQueryParams();
  const sessionId = queryParams.get('session_id') || undefined;
  const { data } = useAnvaQuotesWithPolling({
    sessionId,
    enabled: !!sessionId,
  });

  const {
    getNextProductUrl,
    getPreviousProductUrl,
    isFirstIndex,
    isFirstTag,
    tagId,
  } = useFlowProductsSidebarNavigation(data?.navigationMap);

  const isSelectionOverviewPage =
    window.location.pathname.includes('selection-overview');
  const isBasketPage = window.location.pathname.includes('basket');
  const isBasketEmpty = useAnvaFlowProductSelectionStore(
    (state) => !(state.selectedItems.length > 0),
  );
  const history = useHistory();

  const navigateToDashboard = () => {
    history.push('/dashboard');
  };

  const navigateToBasket = () => {
    history.push({
      pathname: `${FLOWS_PRODUCTS_PATH}/${flowId}/basket`,
      search: window.location.search,
    });
  };

  const navigateNextUrl = () => {
    const nextUrl = getNextProductUrl();
    if (nextUrl) {
      history.push(nextUrl);
    } else {
      // Fallback to basket navigation when no further categories are available
      navigateToBasket();
    }
  };

  const navigatePreviousUrl = () => {
    const prevUrl = getPreviousProductUrl();
    if (prevUrl) {
      history.push(prevUrl);
    }
  };

  // Compose left/back button
  let leftButton: ReactNode = null;

  if (isSelectionOverviewPage) {
    // Handled separately further down (Back to dashboard)
  } else if (isBasketPage) {
    // Keep original behaviour for basket page
    leftButton = (
      <Button
        flexShrink={0}
        onClick={handlePrevious}
        leftIcon={<Icon name="GeneralActionsArrowLeft" />}
        variant="white"
      >
        {backText}
      </Button>
    );
  } else {
    // Overview page – cycle through indices/categories or go back to questions
    const isFirstIndexAndCategory = isFirstIndex() && isFirstTag();
    const label = isFirstIndexAndCategory ? backText : t('Previous');
    const onClick = isFirstIndexAndCategory
      ? handlePrevious
      : navigatePreviousUrl;

    leftButton = (
      <Button
        flexShrink={0}
        onClick={onClick}
        leftIcon={<Icon name="GeneralActionsArrowLeft" />}
        variant="white"
      >
        {label}
      </Button>
    );
  }

  // Compose primary (right) action button for overview page
  let rightButton: ReactNode = null;

  if (!isBasketPage && !isSelectionOverviewPage && tagId) {
    const isLastIndexAndCategory = !getNextProductUrl();

    const disableButton = isLastIndexAndCategory ? isBasketEmpty : false;
    const onClick = isLastIndexAndCategory ? navigateToBasket : navigateNextUrl;
    const labelKey = isLastIndexAndCategory
      ? 'CAMPAIGNS_DETAIL-CHOSEN_PRODUCTS'
      : 'NEXT';

    rightButton = (
      <Button
        flexShrink={0}
        ml="auto"
        onClick={onClick}
        isDisabled={disableButton}
        rightIcon={<Icon name="GeneralActionsArrowRight" />}
      >
        {t(labelKey)}
      </Button>
    );
  }

  return (
    <BottomBarWrapper>
      {isSelectionOverviewPage && !isCampaignsUrl && (
        <Button
          flexShrink={0}
          onClick={navigateToDashboard}
          leftIcon={<Icon name="GeneralActionsArrowLeft" />}
          variant="white"
        >
          {t('BACK-TO-DASHBOARD')}
        </Button>
      )}
      {leftButton}
      {rightButton}
      {isBasketPage && <BottomBarBasketActions />}
      {isSelectionOverviewPage && (
        <BottomBarSelectionOverviewActions flowId={flowId} />
      )}
    </BottomBarWrapper>
  );
};

export default AnvaFlowProductsBottomBar;
